import React, { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { observer } from "mobx-react";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { translations } from "@/constants/translations";
import type { RegisterDetailsModel } from "@/store/types/register";
import { useStore } from "@/store";
import { AddressLookupSelect } from "@/components/AddressLookupSelect";
import type { AddressLookupDetailsDto } from "@/api/types";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { stripColon } from "@/helpers/string";
import { H2, H3 } from "@/components/Heading";
import { FormInput } from "@/components/Form/FormInput";
import {
  getBusinessTypes,
  getOrgTypes,
  getSecondsSinceTermsViewed,
} from "../helpers";
import { FormGroup } from "@/components/Form/FormGroup";
import { telemetry } from "@/telemetry";

export const RegisterFormPage = observer(() => {
  const { registerData, setRegisterData, activeLanguage, getErrorInputText } =
    useStore();
  const language = translations.pages["account-registration"][activeLanguage];
  const navigate = useAppNavigate();
  const hasStarted = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    setValue,
    getValues,
    control,
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: registerData,
  });

  const isFormValid =
    isValid &&
    watch("organizationTypes").length > 0 &&
    watch("organizationActivities").length > 0;

  const handleAddressSelect = (data: AddressLookupDetailsDto) => {
    setValue("city", data.city);
    setValue("provinceTerritory", data.province);
    setValue("postalCode", data.postalCode);
    setValue("streetAddress", data.text);
  };

  const handleFormChange = () => {
    if (hasStarted.current) return;

    hasStarted.current = true;
    telemetry.trackEvent("registration_form_started", {
      seconds_since_terms: getSecondsSinceTermsViewed(),
    });
  };

  const trackValidationFailed = (
    formErrors: FieldErrors<RegisterDetailsModel>,
  ) => {
    const failedFields = Object.keys(formErrors);

    if (getValues("organizationTypes").length === 0)
      failedFields.push("organizationTypes");
    if (getValues("organizationActivities").length === 0)
      failedFields.push("organizationActivities");

    telemetry.trackEvent("registration_validation_failed", {
      failed_fields: failedFields.join(","),
      count: failedFields.length,
    });
  };

  const onSubmit = async (values: RegisterDetailsModel) => {
    if (
      values.organizationTypes.length === 0 ||
      values.organizationActivities.length === 0
    ) {
      trackValidationFailed({});
      return;
    }

    telemetry.trackEvent("registration_form_submitted", {
      organization_types: values.organizationTypes.join(","),
      activities_count: values.organizationActivities.length,
      has_ownership: !!values.organizationOwnershipName,
      has_liens: !!values.hasSuitsLiensJudgements,
    });
    setRegisterData({
      ...values,
    });
    navigate({
      to: "/register/review",
    });
  };

  return (
    <Stack spacing="14">
      <H2 textAlign="center">{language.please_fill}</H2>
      <Stack
        as="form"
        spacing="14"
        onSubmit={handleSubmit(onSubmit, trackValidationFailed)}
        onChangeCapture={handleFormChange}
      >
        {/* Organization contact informion */}
        <Stack spacing="8">
          <H3>{language.organization_contact}</H3>
          <SimpleGrid
            columns={{ sm: 1, md: 2 }}
            spacing="6"
          >
            <FormInput
              label={language.organizations_legal_name}
              error={errors.organizationLegalName}
              {...register("organizationLegalName", {
                required: true,
              })}
            />

            <FormInput
              label={language.operating_name}
              error={errors.operatingName}
              {...register("operatingName", {
                required: true,
              })}
            />

            <FormGroup
              label={language.street_address}
              error={errors.streetAddress}
            >
              <Controller
                name="streetAddress"
                control={control}
                render={({ field: { onBlur, onChange, value } }) => (
                  <AddressLookupSelect
                    name="streetAddress"
                    onSelect={(data) => {
                      handleAddressSelect(data);
                      onChange(data.text);
                    }}
                    onBlur={onBlur}
                    value={value}
                    placeholder={language.street_address}
                  />
                )}
                rules={{ required: getErrorInputText(language.street_address) }}
              />
            </FormGroup>

            <FormInput
              label={language.unit_suit}
              isReadOnly
              {...register("unitSuite")}
            />
          </SimpleGrid>

          <SimpleGrid
            columns={{ sm: 1, md: 3 }}
            spacing="6"
          >
            <FormInput
              label={language.city}
              isReadOnly
              {...register("city")}
            />
            <FormInput
              label={language.province}
              isReadOnly
              {...register("provinceTerritory")}
            />
            <FormInput
              label={language.postal_code}
              isReadOnly
              {...register("postalCode")}
            />
          </SimpleGrid>
        </Stack>

        {/* Billing contact information: */}
        <Stack spacing="8">
          <H3>{language.billing_contact_info}</H3>
          <SimpleGrid
            columns={{ sm: 1, md: 2 }}
            gap="6"
          >
            <FormInput
              label={language.billing_name}
              error={errors.billingContactName}
              {...register("billingContactName", {
                required: true,
              })}
            />
            <FormInput
              label={language.billing_title}
              error={errors.billingContactTitle}
              {...register("billingContactTitle", {
                required: true,
              })}
            />
          </SimpleGrid>

          <SimpleGrid
            columns={{ sm: 1, md: 3 }}
            gap="6"
          >
            <FormInput
              label={language.billing_phone_number}
              error={errors.billingContactPhoneNumber}
              placeholder="(XXX) XXX-XXXX"
              {...register("billingContactPhoneNumber", {
                required: true,
              })}
            />
            <FormInput
              label={language.billing_email_address}
              error={errors.billingContactEmail}
              {...register("billingContactEmail", {
                required: true,
              })}
            />
            <FormInput
              label={language.billing_confirm_email_address}
              error={errors.confirmEmail}
              {...register("confirmEmail", {
                validate: (value, values) =>
                  value === values.billingContactEmail || "Emails must match",
              })}
            />
          </SimpleGrid>
        </Stack>

        {/* Organization type and ownership: */}
        <Stack spacing="8">
          <H3>{language.organization_type_ownership}</H3>
          <SimpleGrid
            columns={{ sm: 1, md: 2 }}
            spacing="6"
          >
            <Stack spacing="8">
              <Stack spacing="4">
                <FormLabel fontWeight="bold">
                  {language.organization_type}
                </FormLabel>
                <Stack>
                  {getOrgTypes(activeLanguage).map((option) => (
                    <Checkbox
                      key={option.value}
                      id={option.value}
                      value={option.value}
                      {...register("organizationTypes")}
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </Stack>
              </Stack>
              <FormInput
                label={language.tax_id}
                {...register("taxID")}
              />
              <FormInput
                label={language.location_of_incorporation}
                error={errors.locationOfIncorporation}
                {...register("locationOfIncorporation", {
                  required: true,
                })}
              />
            </Stack>
            <Stack spacing="8">
              <Stack spacing="4">
                <FormLabel fontWeight="bold">
                  {language.suits_liens_judgements}
                </FormLabel>
                <Text>{language.are_there_suits}</Text>
                <Controller
                  control={control}
                  name="hasSuitsLiensJudgements"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <CheckboxGroup>
                      <Stack>
                        <Checkbox
                          id="hasSuitsLiensJudgements"
                          onChange={() => onChange(true)}
                          onBlur={onBlur}
                          isChecked={value === true}
                        >
                          {language.are_there_suits_yes}
                        </Checkbox>
                        <Checkbox
                          id="hasSuitsLiensJudgements"
                          onChange={() => onChange(false)}
                          onBlur={onBlur}
                          isChecked={value === false}
                        >
                          {language.are_there_suits_no}
                        </Checkbox>
                      </Stack>
                    </CheckboxGroup>
                  )}
                />
                <Stack>
                  <Text>{language.if_yes_description}</Text>
                  <Textarea
                    rows={7}
                    {...register("suitsLiensJudgementsDescription")}
                  />
                </Stack>
              </Stack>
            </Stack>
          </SimpleGrid>
        </Stack>

        {/* Organization ownership */}
        <Stack spacing="8">
          <Text fontWeight="bold">{language.organization_ownership}</Text>
          <SimpleGrid
            columns={{ sm: 1, md: 2 }}
            spacing="6"
          >
            <FormInput
              label={language.owner_name}
              error={errors.organizationOwnershipName}
              {...register("organizationOwnershipName", {
                required: true,
              })}
            />
            <FormInput
              label={language.owner_title}
              error={errors.organizationOwnershipTitle}
              {...register("organizationOwnershipTitle", {
                required: true,
              })}
            />
            <FormInput
              label={language.owner_ownership}
              error={errors.organizationOwnershipPercentage}
              {...register("organizationOwnershipPercentage", {
                required: true,
              })}
            />
            <FormGroup
              label={language.home_address}
              error={errors.organizationOwnershipHomeAddress}
            >
              <Controller
                name="organizationOwnershipHomeAddress"
                control={control}
                render={({ field: { onBlur, onChange, value } }) => (
                  <AddressLookupSelect
                    name="organizationOwnershipHomeAddress"
                    onSelect={(data) => {
                      const address = `${data.text}, ${data.city}, ${data.province}, ${data.postalCode}`;
                      setValue("organizationOwnershipHomeAddress", address);
                      onChange(address);
                    }}
                    onBlur={onBlur}
                    value={value}
                    placeholder={language.home_address}
                  />
                )}
                rules={{ required: getErrorInputText(language.home_address) }}
              />
            </FormGroup>
          </SimpleGrid>
        </Stack>

        {/* Organization's activities: */}
        <Stack spacing="8">
          <H3>{language.organizations_activities}</H3>
          <Stack spacing="4">
            <Text fontWeight="bold">{language.type_of_business}</Text>
            <SimpleGrid
              columns={{ sm: 1, md: 3 }}
              spacingX="6"
              spacingY="4"
            >
              {getBusinessTypes(activeLanguage).map((option) => (
                <HStack
                  key={option.value}
                  gap="3"
                >
                  <Checkbox
                    value={option.value}
                    {...register("organizationActivities")}
                  >
                    {option.label}
                  </Checkbox>
                  {option.value === "Other" && (
                    <Input
                      {...register("orgActivityOtherDesc")}
                      isDisabled={
                        !watch("organizationActivities").includes("Other")
                      }
                    />
                  )}
                </HStack>
              ))}
            </SimpleGrid>
          </Stack>

          <Stack spacing="4">
            <Text fontWeight="bold">{language.is_the_purchasing}</Text>
            <Controller
              control={control}
              name="isBuyingOnBehalfOfThirdParty"
              render={({ field: { onChange, onBlur, value } }) => (
                <Stack>
                  <HStack>
                    <Checkbox
                      id="isBuyingOnBehalfOfThirdPartyTrue"
                      onChange={() => onChange(true)}
                      onBlur={onBlur}
                      isChecked={value === true}
                    >
                      {language.is_the_purchasing_yes}
                    </Checkbox>
                    <Input
                      width="200px"
                      placeholder={stripColon(language.more_details)}
                      isDisabled={!getValues("isBuyingOnBehalfOfThirdParty")}
                    />
                  </HStack>
                  <Checkbox
                    id="isBuyingOnBehalfOfThirdPartyFalse"
                    onChange={() => onChange(false)}
                    onBlur={onBlur}
                    isChecked={value === false}
                  >
                    {language.is_the_purchasing_no}
                  </Checkbox>
                </Stack>
              )}
            />
          </Stack>
        </Stack>

        <HStack justifyContent="flex-end">
          <Button
            type="submit"
            aria-disabled={!isFormValid}
          >
            {language.review_registration_details}
          </Button>
        </HStack>
      </Stack>
    </Stack>
  );
});
